import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import BoundImageModal from '../../components/boundImageModal';
import {StyledPageCenterContainer, pxToEm} from '../../components/globalStyles';
import HexagonIconImage from '../../components/hexagonIconImage';
import Layout from '../../components/layout/layout';
import PageViewHeadline from '../../components/pageViewHeadline';
import SeeItInActionButton from '../../components/seeItInActionButton';
import WhoIsUsingSection from '../../components/whoIsUsingSection';
import {CompanyName} from '../../data/customerData';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {Product, ProductToHexagonIconSrc} from '../../data/product';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import Bitmovin8ppEncodingGraphicSvg from '../../media/cloudEncodingPageView/bitmovin_8pp_encoding_graphic.svg';
import QualityComparisonSvg from '../../media/cloudEncodingPageView/quality_comparison_updated.png';
import SpeedEncodingPng from '../../media/cloudEncodingPageView/speed_encoding_updated.png';
import StartDemoSvg from '../../media/hexagonIconImage/icons/start_demo.svg';

import CloudEncodingAccordions from './encodingAccordions';
import NoteworthyFeaturesSection from './noteworthyFeaturesSection';

//#region Styled Components

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 50%;
`;

const StyledTwoColumnsSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > ${StyledContentColumn} {
    flex: 1 1 50%;

    :nth-child(1) {
      border-right: 2px solid ${baseVariables.colors.darkBlueGray};
      padding-right: 5%;
    }

    :nth-child(2) {
      padding-left: 5%;
    }
  }
`;

const StyledContentTwoColumnsSection = styled(StyledTwoColumnsSection)`
  padding-top: ${pxToEm(89)}em;
  padding-bottom: ${pxToEm(114)}em;
`;

const StyledEncodingButtonsDiv = styled.div`
  display: flex;
  margin-top: ${pxToEm(30)}em;

  > * {
    :not(:first-child) {
      margin-left: 1em;
    }
  }
`;

const StyledImageModal = styled(BoundImageModal)`
  margin-top: ${pxToEm(86)}em;
`;

const StyledImage = styled.img`
  width: 100%;
`;

const StyledH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  color: ${baseVariables.colors.midnight};
  display: inline-block;
  margin: 0 0 ${pxToEm(55) / pxToEm(24)}em;
`;

const StyledAccordions = styled(CloudEncodingAccordions)`
  margin-top: ${pxToEm(70)}em;
`;

const StyledP = styled.p`
  font-size: ${pxToEm(20)}em;
  line-height: 1.6;
  margin: 0;
`;

const StyledDemoLinkButton = styled(Button)`
  margin-top: ${pxToEm(30)}em;
`;

const StyledListWrapperDiv = styled.div`
  width: 100%;
  padding: 0 ${pxToEm(20)}em;
  margin-bottom: ${pxToEm(30)}em;
`;

//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {}

class CloudEncodingPageView extends React.Component<Props> {
  public render() {
    return (
      <Layout>
        <PageViewHeadline
          topSubHeadline={'Next-Generation VOD Encoder'}
          headline={'A new standard of quality'}
          badgeComponent={<HexagonIconImage iconSrc={ProductToHexagonIconSrc[Product.Encoding]} />}
          leftBadgeComponent={this.renderSeeItInActionButton()}
        />

        <StyledPageCenterContainer>
          <StyledContentTwoColumnsSection>
            <StyledContentColumn>
              <StyledListWrapperDiv>
                <StyledH2>Explore Bitmovin Encoding</StyledH2>
                <StyledP>
                  The Bitmovin Next-Generation VOD Encoder delivers the highest quality of experience on the market
                  today.
                </StyledP>
              </StyledListWrapperDiv>
              <NoteworthyFeaturesSection />
            </StyledContentColumn>

            <StyledContentColumn>
              <StyledH2>Horizontal scalability multiplies performance</StyledH2>
              <StyledP>
                Take a video, split it into chunks, and encode chunks simultaneously across as many different instances
                as you like. Containerized encoding gives you the power and control to scale up and down on the fly, as
                your workflow requires.
              </StyledP>

              <StyledEncodingButtonsDiv>
                <BoundImageModal
                  boundComponent={<Button variant={ButtonVariant.OutlinedLight}>Encoding speed</Button>}
                  source={SpeedEncodingPng}
                />
                <BoundImageModal
                  boundComponent={<Button variant={ButtonVariant.OutlinedLight}>Quality Comparison</Button>}
                  source={QualityComparisonSvg}
                />
              </StyledEncodingButtonsDiv>

              <StyledImageModal
                boundComponent={<StyledImage src={Bitmovin8ppEncodingGraphicSvg} alt="bitmovin 8pp encoding graphic" />}
                source={Bitmovin8ppEncodingGraphicSvg}
              />
              <StyledAccordions />
            </StyledContentColumn>
          </StyledContentTwoColumnsSection>
        </StyledPageCenterContainer>
        <WhoIsUsingSection
          headline={'Customer proof points'}
          companies={[CompanyName.BBC, CompanyName.NewYorkTimes, CompanyName.Globo]}
        />
      </Layout>
    );
  }

  private renderSeeItInActionButton = () => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === HardcodedSalesDemoPath.PerTitleEncodingDemo);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <SeeItInActionButton
        external={true}
        hexagonIconSrc={StartDemoSvg}
        to="https://bitmovin.com/dashboard/encoding/demos"
      />
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(CloudEncodingPageView);
